import { useState, useEffect, lazy, Suspense } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Trash2, ImageIcon, Crop, RotateCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SafeImage } from '@/components/SafeImage';
import { useImageCompression } from '@/hooks/useImageCompression';
const ImageCropDialog = lazy(() => import('@/components/ImageCropDialog').then(m => ({ default: m.ImageCropDialog })));
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface EditableImageProps {
  src: string;
  alt: string;
  className?: string;
  onImageUpdate?: (newUrl: string) => Promise<void>;
  onImageDelete?: () => Promise<void>;
  onOriginalSaved?: (originalUrl: string) => Promise<void>;
  allowDelete?: boolean;
  bucket?: string;
  path?: string;
  originalPath?: string;
  originalSrc?: string;
  loading?: 'lazy' | 'eager';
  aspectRatio?: number;
  fetchPriority?: 'high' | 'low' | 'auto';
}

export const EditableImage = ({ 
  src, 
  alt, 
  className = '', 
  onImageUpdate,
  onImageDelete,
  onOriginalSaved,
  allowDelete = true,
  bucket = 'site-assets',
  path,
  originalPath,
  originalSrc,
  loading = 'lazy',
  aspectRatio,
  fetchPriority
}: EditableImageProps) => {
  const { isAdmin } = useAdmin();
  const [isUploading, setIsUploading] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const { toast } = useToast();
  const { compressImage, isCompressing } = useImageCompression();

  useEffect(() => {
    if (src && src !== currentSrc) {
      const srcBase = src.split('?')[0];
      const currentBase = currentSrc?.split('?')[0] || '';
      if (srcBase !== currentBase || (src.includes('?t=') && src !== currentSrc)) {
        setCurrentSrc(src);
      }
    }
  }, [src]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['jpg', 'jpeg', 'png', 'webp'];
    if (!fileExt || !allowed.includes(fileExt)) {
      toast({ title: "Format non accepté", description: "Utilisez JPG, PNG ou WEBP.", variant: "destructive" });
      event.target.value = '';
      return;
    }

    // If we have an originalPath, save the original first
    if (originalPath) {
      await saveOriginal(file);
    }

    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setFileToUpload(file);
    setCropDialogOpen(true);
    event.target.value = '';
  };

  const saveOriginal = async (file: File) => {
    if (!originalPath) return;
    try {
      const { file: compressedFile } = await compressImage(file, {
        maxWidth: 2400, maxHeight: 2400, quality: 0.92, maxSizeMB: 4
      });

      const { error } = await supabase.storage
        .from(bucket)
        .upload(originalPath, compressedFile, { upsert: true, contentType: compressedFile.type });

      if (error) {
        console.error('Error saving original:', error);
        return;
      }

      const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(originalPath);
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      if (onOriginalSaved) {
        await onOriginalSaved(cacheBustedUrl);
      }
    } catch (error) {
      console.error('Error saving original image:', error);
    }
  };

  const handleCropExisting = async () => {
    // Always crop from original if available
    const sourceForCrop = originalSrc || currentSrc;
    if (!sourceForCrop) return;

    // If no original saved yet, save the current image as original before cropping
    if (!originalSrc && currentSrc && originalPath) {
      try {
        const response = await fetch(currentSrc);
        const blob = await response.blob();
        const file = new File([blob], 'original.jpg', { type: blob.type });
        await saveOriginal(file);
      } catch (err) {
        console.error('Failed to save original before crop:', err);
      }
    }

    setImageToCrop(sourceForCrop);
    setCropDialogOpen(true);
  };

  const handleRestoreOriginal = async () => {
    if (!originalSrc) return;
    setIsUploading(true);
    try {
      setCurrentSrc(originalSrc);
      if (onImageUpdate) await onImageUpdate(originalSrc);
      toast({ title: "Image restaurée", description: "L'image originale a été restaurée." });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de restaurer l'image", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    const fileName = fileToUpload?.name || 'cropped-image.jpg';
    const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
    await uploadImage(croppedFile);
    if (imageToCrop && !imageToCrop.startsWith('http')) {
      URL.revokeObjectURL(imageToCrop);
    }
    setImageToCrop(null);
    setFileToUpload(null);
  };

  const uploadImage = async (file: File) => {
    setIsUploading(true);
    try {
      const { file: compressedFile, compressionRatio } = await compressImage(file, {
        maxWidth: 1920, maxHeight: 1920, quality: 0.85, maxSizeMB: 2
      });

      if (compressedFile.size > 2 * 1024 * 1024) {
        toast({ title: "Image trop volumineuse", description: "Même après compression, l'image dépasse 2MB.", variant: "destructive" });
        return;
      }

      const fileExt = compressedFile.name.split('.').pop();
      const filePath = path || `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, compressedFile, { upsert: true, contentType: compressedFile.type });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(filePath);
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;

      setCurrentSrc(cacheBustedUrl);
      if (onImageUpdate) await onImageUpdate(cacheBustedUrl);

      toast({ title: "Image mise à jour", description: compressionRatio > 5 ? `Compressée de ${compressionRatio.toFixed(0)}%` : "Téléversée avec succès" });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({ title: "Erreur", description: "Impossible de téléverser l'image", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!onImageDelete) return;
    setIsUploading(true);
    try {
      await onImageDelete();
      setCurrentSrc('');
      toast({ title: "Image supprimée" });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer l'image", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const hasImage = currentSrc && currentSrc.trim() !== '';
  const hasOriginal = originalSrc && originalSrc.trim() !== '';
  const inputId = `upload-${Math.random().toString(36).substring(7)}`;

  return (
    <div className="relative group w-full h-full">
      {hasImage ? (
        <SafeImage src={currentSrc} alt={alt} className={className} fallbackText="Image non trouvée" loading={loading} fetchPriority={fetchPriority} />
      ) : (
        <div className={`${className} bg-muted flex flex-col items-center justify-center border-2 border-dashed border-border/50 hover:border-primary/50 transition-all`}>
          <ImageIcon className="w-12 h-12 text-muted-foreground/50 mb-2" />
          <span className="text-muted-foreground text-sm">Aucune image</span>
        </div>
      )}
      
      {isAdmin && (
        <>
          <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleFileSelect} className="hidden" id={inputId} disabled={isUploading} />
          
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 p-2 bg-background/70 backdrop-blur-sm z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <TooltipProvider delayDuration={200}>
              {/* Upload / Change */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <label htmlFor={inputId}>
                    <Button size="icon" variant="secondary" className="h-8 w-8 cursor-pointer" disabled={isUploading || isCompressing} asChild>
                      <span>{isUploading || isCompressing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}</span>
                    </Button>
                  </label>
                </TooltipTrigger>
                <TooltipContent side="top"><p>Changer</p></TooltipContent>
              </Tooltip>

              {/* Crop */}
              {hasImage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCropExisting(); }} disabled={isUploading}>
                      <Crop className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Recadrer{hasOriginal ? ' (depuis l\'original)' : ''}</p></TooltipContent>
                </Tooltip>
              )}

              {/* Restore original */}
              {hasOriginal && hasImage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="secondary" className="h-8 w-8" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRestoreOriginal(); }} disabled={isUploading}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Restaurer l'original</p></TooltipContent>
                </Tooltip>
              )}

              {/* Delete */}
              {allowDelete && onImageDelete && hasImage && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="destructive" className="h-8 w-8" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(); }} disabled={isUploading}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top"><p>Supprimer</p></TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        </>
      )}

      {imageToCrop && (
        <Suspense fallback={null}>
          <ImageCropDialog
            open={cropDialogOpen}
            onOpenChange={(open) => {
              setCropDialogOpen(open);
              if (!open && imageToCrop && !imageToCrop.startsWith('http')) {
                URL.revokeObjectURL(imageToCrop);
                setImageToCrop(null);
                setFileToUpload(null);
              }
            }}
            imageSrc={imageToCrop}
            onCropComplete={handleCropComplete}
            aspect={aspectRatio}
          />
        </Suspense>
      )}
    </div>
  );
};
